import { Icon } from "@brg/icons-next";
import React, { FC, useCallback, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Avatar, Colors, FInter, FlexBox, Fonts } from "@brg/ui";
import { useTranslation } from "react-i18next";
import { API, Designer, DesignerAPI } from "@brg/api";
import { computeDesigner } from "../../../../../helpers/computeData";
import { CreateLinkTo } from "../../../../../constants/linkTo";
import { useHistory } from "react-router-dom";
import { NavbarSearchActions } from "shared/reducers/NavbarSearch";
import { useDispatch } from "react-redux";
import { LayoutConst } from "core";

const WAnimation = keyframes`
  from {
    width: 1px;
  }
  to {
    width: 300px;
  }
`;
const WAnimationReverse = keyframes`
  from {
    width: 300px;
  }
  to {
    width: 0;
  }
`;

const WAnimationM = keyframes`
  from {
    width: 1px;
  }
  to {
    width: 250px;
  }
`;
const WAnimationReverseM = keyframes`
  from {
    width: 250px;
  }
  to {
    width: 0;
  }
`;


export const SearchInput = styled.input`
  width: 100%;
  background-color: transparent;
  ${Fonts.Inter.smallText};
  color: ${(p) => p.theme.navigationBar.search.color};
  outline: none !important;
  border: none;
  transition: all 0.2s;
  height: 64px;
`;

export const SearchLine = styled(FlexBox)`
  background: ${(p) => p.theme.navigationBar.search.line};
  height: 2px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;

//todo remove static height

export const SearchWrapper = styled(FlexBox)<{ out?: boolean }>`
  align-items: center;
  height: 64px !important;
  position: relative;
  justify-content: flex-end;
  width: 300px;
  animation: 0.2s linear forwards ${WAnimation};

  @media(max-width: ${LayoutConst.screen.l_16}px) {
    height: 48px !important;
  }

  ${SearchInput} {
    animation: 0.2s linear forwards
      ${(p) => (p.out ? WAnimationReverse : WAnimation)};
  }

  ${SearchLine} {
    animation: 0.2s linear forwards
      ${(p) => (p.out ? WAnimationReverse : WAnimation)};
  }

  @media(max-width: ${LayoutConst.screen.m_1024}px) {
    width: 250px !important;
    animation: 0.2s linear forwards ${WAnimationM};

    ${SearchInput} {
      animation: 0.2s linear forwards
        ${(p) => (p.out ? WAnimationReverseM : WAnimationM)};
    }

    ${SearchLine} {
      animation: 0.2s linear forwards
        ${(p) => (p.out ? WAnimationReverseM : WAnimationM)};
    }
  }

`;

export const SearchFlexBox = styled(FlexBox)`
  height: 64px !important;
  position: relative;
  align-items: center;
  color: ${(p) => p.theme.navigationBar.search.colorFlex};
  min-height: 100%;

  @media(max-width: ${LayoutConst.screen.l_16}px) {
    height: 48px !important;
  }
`;

export const NavbarSearchWrapper = styled.div`
  display: flex;
  cursor: pointer;
  // opacity: 0.47;
  transition: opacity 0.2s;
  align-items: center;
  position: relative;
  svg {
    * {
      fill: ${(p) => p.theme.navigationBar.search.svg} !important;
    }
  }
  &:hover {
    svg {
      * {
        fill: ${(p) => p.theme.navigationBar.search.svgHover} !important;
      }
    }
  }
`;

export const NavbarResultsWrapper = styled.div`
  position: absolute;
  background-color: ${(p) => p.theme.app.backgroundColor};
  max-height: 200px;
  width: 300px;
  top: 64px;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.12);
  border: 1px solid ${(p) => p.theme.app.ten};
  ::-webkit-scrollbar {
    width: 0;
  }
`;

export const NavbarResultsItem = styled.div`
  display: flex;
  width: 300px;
  height: 64px;
  border-bottom: 1px solid ${(p) => p.theme.app.ten};
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: ${(p) => p.theme.app.backgroundColor};
  :hover {
    background-color: ${(p) => p.theme.app.ten};
  }
  :active {
    background-color: ${Colors.galBlack31};
  }
`;

export interface MainMenuSearchBarProps {
  onSearch?: (searchValue: string) => Promise<boolean>;
}

export const useDebouncedEffect = (effect: any, deps: any, delay: number) => {
  useEffect(() => {
    const handler = setTimeout(() => effect(), delay);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(deps || []), delay]);
};

export const NavbarSearchWidget: FC<MainMenuSearchBarProps> = () => {
  const [search, setSearch] = React.useState(false);
  const [isOutAnimation, setIsOutAnimation] = React.useState(false);
  const searchRef = React.useRef<HTMLInputElement>();
  const [designers, setDesigners] = useState<Designer[]>([]);
  const { i18n, t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [isEmpty, setIsEmpty] = useState(false);
  
  const handleToggleVisible = useCallback((flag: boolean) => {
    dispatch(NavbarSearchActions.setNavbarSearchToggle({
      data: flag
    }))
  }, [dispatch])

  useDebouncedEffect(
    () => {
      if (searchText.length >= 2) {
        DesignerAPI.AutoComplete({ key: searchText })
          .then((v) => v.json())
          .then((v) => API.toLang<Designer[]>(v, i18n.language))
          .then((v) => {
            setDesigners(v);
            setIsEmpty(v.length < 1);
          });
      } else {
        setDesigners([]);
      }
    },
    [searchText, i18n.language],
    700
  );


  const onSearchClick = useCallback(() => {
    if (!isOutAnimation) {
      setSearch(true);
      setTimeout(() => {
        searchRef.current?.focus();
      }, 300);
    }
    handleToggleVisible(true)
  }, [isOutAnimation, handleToggleVisible]);

  const hideSearchBar = useCallback(() => {
    setIsOutAnimation(true);
    handleToggleVisible(false)
  }, [handleToggleVisible]);

  const resetSearchMode = useCallback(() => {
    if (isOutAnimation) {
      setSearch(false);
      setSearchText("");
      setIsOutAnimation(false);
      handleToggleVisible(false)
    }
  }, [isOutAnimation, handleToggleVisible]);

  const onSearchResultItemClick = useCallback((id: string) => {
    history.push(CreateLinkTo.designer(id));
    resetSearchMode();
  }, [history, resetSearchMode]);

  return (
    <SearchFlexBox>
      <SearchWrapper out={isOutAnimation}>
        {search && (
          <>
            <SearchInput
              onBlur={hideSearchBar}
              onAnimationEnd={resetSearchMode}
              ref={searchRef as any}
              placeholder={t("search")}
              onInput={(e: any) => setSearchText(e.target.value?.trim())}
            />
            <SearchLine />
          </>
        )}

        <NavbarSearchWrapper>
          <Icon.Search24 onClick={onSearchClick} />
        </NavbarSearchWrapper>

        {searchText.length > 0 && search ? (
          <NavbarResultsWrapper>
            {designers.map((v) => {
              const cv = computeDesigner(v);
              return (
                <NavbarResultsItem
                  onClick={() => onSearchResultItemClick(v.accountId)}
                  key={v.accountId}
                >
                  <FlexBox align={"center"} gx={8}>
                    <FlexBox style={{ padding: "4px" }} width={64} height={64}>
                      <Avatar {...cv} />
                    </FlexBox>
                    <FInter.SmallTextDiv>{cv.fullName}</FInter.SmallTextDiv>
                  </FlexBox>
                </NavbarResultsItem>
              );
            })}
            {designers.length < 1 && searchText.length < 2 && (
              <NavbarResultsItem>
                <FlexBox
                  align={"center"}
                  width={"100%"}
                  justify={"center"}
                  gx={8}
                >
                  <FInter.SmallTextDiv>
                    {t("text.more2chars")}
                  </FInter.SmallTextDiv>
                </FlexBox>
              </NavbarResultsItem>
            )}
            {designers.length < 1 && searchText.length >= 2 && !isEmpty && (
              <NavbarResultsItem>
                <FlexBox
                  align={"center"}
                  width={"100%"}
                  justify={"center"}
                  gx={8}
                >
                  <FInter.SmallTextDiv>{t("text.loading")}</FInter.SmallTextDiv>
                </FlexBox>
              </NavbarResultsItem>
            )}
            {designers.length < 1 && searchText.length >= 2 && isEmpty && (
              <NavbarResultsItem>
                <FlexBox
                  align={"center"}
                  width={"100%"}
                  justify={"center"}
                  gx={8}
                >
                  <FInter.SmallTextDiv>
                    {t("text.notfound")}
                  </FInter.SmallTextDiv>
                </FlexBox>
              </NavbarResultsItem>
            )}
          </NavbarResultsWrapper>
        ) : null}
      </SearchWrapper>
    </SearchFlexBox>
  );
};
import { Icon } from "@brg/icons-next";
import React, { FC, useCallback, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Avatar, Colors, FInter, FlexBox, Fonts } from "@brg/ui";
import { useTranslation } from "react-i18next";
import { API, Designer, DesignerAPI } from "@brg/api";
import { computeDesigner } from "../../../../../helpers/computeData";
import { CreateLinkTo } from "../../../../../constants/linkTo";
import { useHistory } from "react-router-dom";
import { NavbarSearchActions } from "shared/reducers/NavbarSearch";
import { useDispatch } from "react-redux";
import { LayoutConst } from "core";

const WAnimation = keyframes`
  from {
    width: 1px;
  }
  to {
    width: 300px;
  }
`;
const WAnimationReverse = keyframes`
  from {
    width: 300px;
  }
  to {
    width: 0;
  }
`;

const WAnimationM = keyframes`
  from {
    width: 1px;
  }
  to {
    width: 250px;
  }
`;
const WAnimationReverseM = keyframes`
  from {
    width: 250px;
  }
  to {
    width: 0;
  }
`;


export const SearchInput = styled.input`
  width: 100%;
  background-color: transparent;
  ${Fonts.Inter.smallText};
  color: ${(p) => p.theme.navigationBar.search.color};
  outline: none !important;
  border: none;
  transition: all 0.2s;
  height: 64px;
`;

export const SearchLine = styled(FlexBox)`
  background: ${(p) => p.theme.navigationBar.search.line};
  height: 2px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;

//todo remove static height

export const SearchWrapper = styled(FlexBox)<{ out?: boolean }>`
  align-items: center;
  height: 64px !important;
  position: relative;
  justify-content: flex-end;
  width: 300px;
  animation: 0.2s linear forwards ${WAnimation};

  @media(max-width: ${LayoutConst.screen.l_16}px) {
    height: 48px !important;
  }

  ${SearchInput} {
    animation: 0.2s linear forwards
      ${(p) => (p.out ? WAnimationReverse : WAnimation)};
  }

  ${SearchLine} {
    animation: 0.2s linear forwards
      ${(p) => (p.out ? WAnimationReverse : WAnimation)};
  }

  @media(max-width: ${LayoutConst.screen.m_1024}px) {
    width: 250px !important;
    animation: 0.2s linear forwards ${WAnimationM};

    ${SearchInput} {
      animation: 0.2s linear forwards
        ${(p) => (p.out ? WAnimationReverseM : WAnimationM)};
    }

    ${SearchLine} {
      animation: 0.2s linear forwards
        ${(p) => (p.out ? WAnimationReverseM : WAnimationM)};
    }
  }

`;

export const SearchFlexBox = styled(FlexBox)`
  height: 64px !important;
  position: relative;
  align-items: center;
  color: ${(p) => p.theme.navigationBar.search.colorFlex};
  min-height: 100%;

  @media(max-width: ${LayoutConst.screen.l_16}px) {
    height: 48px !important;
  }
`;

export const NavbarSearchWrapper = styled.div`
  display: flex;
  cursor: pointer;
  // opacity: 0.47;
  transition: opacity 0.2s;
  align-items: center;
  position: relative;
  svg {
    * {
      fill: ${(p) => p.theme.navigationBar.search.svg} !important;
    }
  }
  &:hover {
    svg {
      * {
        fill: ${(p) => p.theme.navigationBar.search.svgHover} !important;
      }
    }
  }
`;

export const NavbarResultsWrapper = styled.div`
  position: absolute;
  background-color: ${(p) => p.theme.app.backgroundColor};
  max-height: 200px;
  width: 300px;
  top: 64px;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.12);
  border: 1px solid ${(p) => p.theme.app.ten};
  ::-webkit-scrollbar {
    width: 0;
  }
`;

export const NavbarResultsItem = styled.div`
  display: flex;
  width: 300px;
  height: 64px;
  border-bottom: 1px solid ${(p) => p.theme.app.ten};
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: ${(p) => p.theme.app.backgroundColor};
  :hover {
    background-color: ${(p) => p.theme.app.ten};
  }
  :active {
    background-color: ${Colors.galBlack31};
  }
`;

export interface MainMenuSearchBarProps {
  onSearch?: (searchValue: string) => Promise<boolean>;
}

export const useDebouncedEffect = (effect: any, deps: any, delay: number) => {
  useEffect(() => {
    const handler = setTimeout(() => effect(), delay);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(deps || []), delay]);
};

export const NavbarSearchWidget: FC<MainMenuSearchBarProps> = () => {
  const [search, setSearch] = React.useState(false);
  const [isOutAnimation, setIsOutAnimation] = React.useState(false);
  const searchRef = React.useRef<HTMLInputElement>();
  const [designers, setDesigners] = useState<Designer[]>([]);
  const { i18n, t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [isEmpty, setIsEmpty] = useState(false);
  
  const handleToggleVisible = useCallback((flag: boolean) => {
    dispatch(NavbarSearchActions.setNavbarSearchToggle({
      data: flag
    }))
  }, [dispatch])

  useDebouncedEffect(
    () => {
      if (searchText.length >= 2) {
        DesignerAPI.AutoComplete({ key: searchText })
          .then((v) => v.json())
          .then((v) => API.toLang<Designer[]>(v, i18n.language))
          .then((v) => {
            setDesigners(v);
            setIsEmpty(v.length < 1);
          });
      } else {
        setDesigners([]);
      }
    },
    [searchText, i18n.language],
    700
  );


  const onSearchClick = useCallback(() => {
    if (!isOutAnimation) {
      setSearch(true);
      setTimeout(() => {
        searchRef.current?.focus();
      }, 300);
    }
    handleToggleVisible(true)
  }, [isOutAnimation, handleToggleVisible]);

  const hideSearchBar = useCallback(() => {
    setIsOutAnimation(true);
    handleToggleVisible(false)
  }, [handleToggleVisible]);

  const resetSearchMode = useCallback(() => {
    if (isOutAnimation) {
      setSearch(false);
      setSearchText("");
      setIsOutAnimation(false);
      handleToggleVisible(false)
    }
  }, [isOutAnimation, handleToggleVisible]);

  const onSearchResultItemClick = useCallback((id: string) => {
    history.push(CreateLinkTo.designer(id));
    resetSearchMode();
  }, [history, resetSearchMode]);

  return (
    <SearchFlexBox>
      <SearchWrapper out={isOutAnimation}>
        {search && (
          <>
            <SearchInput
              onBlur={hideSearchBar}
              onAnimationEnd={resetSearchMode}
              ref={searchRef as any}
              placeholder={t("search")}
              onInput={(e: any) => setSearchText(e.target.value?.trim())}
            />
            <SearchLine />
          </>
        )}

        <NavbarSearchWrapper>
          <Icon.Search24 onClick={onSearchClick} />
        </NavbarSearchWrapper>

        {searchText.length > 0 && search ? (
          <NavbarResultsWrapper>
            {designers.map((v) => {
              const cv = computeDesigner(v);
              return (
                <NavbarResultsItem
                  onClick={() => onSearchResultItemClick(v.accountId)}
                  key={v.accountId}
                >
                  <FlexBox align={"center"} gx={8}>
                    <FlexBox style={{ padding: "4px" }} width={64} height={64}>
                      <Avatar {...cv} />
                    </FlexBox>
                    <FInter.SmallTextDiv>{cv.fullName}</FInter.SmallTextDiv>
                  </FlexBox>
                </NavbarResultsItem>
              );
            })}
            {designers.length < 1 && searchText.length < 2 && (
              <NavbarResultsItem>
                <FlexBox
                  align={"center"}
                  width={"100%"}
                  justify={"center"}
                  gx={8}
                >
                  <FInter.SmallTextDiv>
                    {t("text.more2chars")}
                  </FInter.SmallTextDiv>
                </FlexBox>
              </NavbarResultsItem>
            )}
            {designers.length < 1 && searchText.length >= 2 && !isEmpty && (
              <NavbarResultsItem>
                <FlexBox
                  align={"center"}
                  width={"100%"}
                  justify={"center"}
                  gx={8}
                >
                  <FInter.SmallTextDiv>{t("text.loading")}</FInter.SmallTextDiv>
                </FlexBox>
              </NavbarResultsItem>
            )}
            {designers.length < 1 && searchText.length >= 2 && isEmpty && (
              <NavbarResultsItem>
                <FlexBox
                  align={"center"}
                  width={"100%"}
                  justify={"center"}
                  gx={8}
                >
                  <FInter.SmallTextDiv>
                    {t("text.notfound")}
                  </FInter.SmallTextDiv>
                </FlexBox>
              </NavbarResultsItem>
            )}
          </NavbarResultsWrapper>
        ) : null}
      </SearchWrapper>
    </SearchFlexBox>
  );
};
